import { useCallback, useRef, useState } from "react"
import type { DecodedVehicle } from "@/lib/api/public-consumer"
import { withTimeout } from "../brain/check-photo"

// The first step of the walk: find out which car it is. The VIN comes from
// the door sticker's barcode (read live), a photo of the sticker (read by the
// server), or typing it. Then one tap: "Is it a 2018 Nissan Altima?". Never a
// wall: a VIN we can't look up is kept for the shop, and the step can be
// skipped (the camera then works the body type out itself).

export type VinHow = "scan" | "photo" | "typed"

export interface VinActions {
  // The VIN read from a photo of the sticker, or null.
  fromPhoto(file: File): Promise<string | null>
  // The car behind a VIN (year, make, model, body type), or null.
  lookup(vin: string): Promise<DecodedVehicle | null>
  // This is the car (found: null = keep the VIN, the shop looks it up).
  confirm(vin: string, found: DecodedVehicle | null, how: VinHow): void
}

export type VinState =
  | { kind: "idle"; note: string | null }
  | { kind: "reading" }
  | { kind: "typing"; error: string | null }
  | { kind: "looking-up"; vin: string; how: VinHow }
  | { kind: "confirm"; vin: string; how: VinHow; found: DecodedVehicle }
  | { kind: "unknown"; vin: string; how: VinHow }

export const VIN_COPY = {
  title: "Your car's VIN",
  instruction: "Point the camera at the sticker on the door frame.",
  walk: "First: open the driver's door",
  scanning: "Point at the barcode on the sticker.",
  reading: "Reading the VIN…",
  lookingUp: "Found it. Looking up your car…",
  confirm: (car: string) => `Is it a ${car}?`,
  yes: "Yes, that's it",
  no: "Not quite",
  unknownTitle: "Got your VIN",
  unknownBody: "We couldn't look the car up just now. The shop will.",
  continue: "Continue",
  type: "Type the VIN",
  skip: "Skip",
  typeTitle: "Type your VIN",
  typeHint: "17 letters and numbers, on the door sticker or at the bottom of the windshield.",
  typeError: "That doesn't look right. Check each character.",
  photoMissed: "Couldn't read the VIN from that photo. Try the barcode, or type it.",
  tryAgain: "Let's try again: point at the barcode, take a photo, or type it.",
  cancel: "Cancel",
  vinLabel: (vin: string) => `VIN ${vin}`,
}

// NHTSA answers in capitals ("NISSAN"); people write "Nissan", but "BMW".
const ACRONYM_MAKES = new Set(["BMW", "GMC", "RAM", "MINI", "MG", "AMC", "SRT", "DS"])

export function makeName(make: string | null | undefined): string {
  return (make ?? "")
    .trim()
    .split(/\s+/)
    .map((word) =>
      ACRONYM_MAKES.has(word.toUpperCase())
        ? word.toUpperCase()
        : word.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase()),
    )
    .join(" ")
}

export function carName(found: DecodedVehicle): string {
  return [found.year, makeName(found.make), found.model].filter(Boolean).join(" ")
}

// How long a sticker read or a lookup may take before the step stops
// waiting (a stalled mobile connection): a missed read, or a VIN kept for the
// shop to look up.
export const VIN_READ_WAIT_MS = 20_000
export const VIN_LOOKUP_WAIT_MS = 12_000

const IDLE: VinState = { kind: "idle", note: null }

export function useVinStep(actions: VinActions, onDone: () => void) {
  const [state, setRendered] = useState<VinState>(IDLE)
  // The state right now, for reads that land between renders.
  const now = useRef<VinState>(IDLE)
  const setState = useCallback((next: VinState) => {
    now.current = next
    setRendered(next)
  }, [])
  // Bumped on every new attempt: a slow answer to an old one is ignored.
  const attempt = useRef(0)
  // VINs they turned down ("Not quite"), stopped waiting for, or chose to
  // type instead of: the barcode reader passes over them (the same sticker
  // is still in view and would ask the same question again).
  const rejected = useRef(new Set<string>())
  const passOver = () => {
    const s = now.current
    if (s.kind === "looking-up" || s.kind === "confirm" || s.kind === "unknown") rejected.current.add(s.vin)
  }

  // A VIN to look up. A scan is only taken while nothing else is going on,
  // and never one they've already turned down. True when it was taken.
  const found = useCallback(
    (vin: string, how: VinHow): boolean => {
      if (how === "scan" && (now.current.kind !== "idle" || rejected.current.has(vin))) return false
      const mine = ++attempt.current
      setState({ kind: "looking-up", vin, how })
      void withTimeout(actions.lookup(vin), VIN_LOOKUP_WAIT_MS).then((car) => {
        if (attempt.current !== mine) return
        setState(car ? { kind: "confirm", vin, how, found: car } : { kind: "unknown", vin, how })
      })
      return true
    },
    [actions, setState],
  )

  // A photo of the sticker, read by the server. Ignored while a read is
  // already under way (the barcode got there first).
  const photo = useCallback(
    (file: File) => {
      if (now.current.kind !== "idle") return
      const mine = ++attempt.current
      setState({ kind: "reading" })
      void withTimeout(actions.fromPhoto(file), VIN_READ_WAIT_MS).then((vin) => {
        if (attempt.current !== mine) return
        if (vin) found(vin, "photo")
        else setState({ kind: "idle", note: VIN_COPY.photoMissed })
      })
    },
    [actions, found, setState],
  )

  const confirm = useCallback(() => {
    const s = now.current
    if (s.kind === "confirm") actions.confirm(s.vin, s.found, s.how)
    else if (s.kind === "unknown") actions.confirm(s.vin, null, s.how)
    else return
    attempt.current++
    setState(IDLE)
    onDone()
  }, [actions, onDone, setState])

  return {
    state,
    // Busy: don't read the barcode again meanwhile.
    busy: state.kind !== "idle",
    found,
    photo,
    confirm,
    reject: () => {
      passOver()
      attempt.current++
      setState({ kind: "idle", note: VIN_COPY.tryAgain })
    },
    type: () => {
      passOver()
      attempt.current++
      setState({ kind: "typing", error: null })
    },
    typed: (vin: string, valid: boolean) => {
      if (valid) found(vin, "typed")
      else setState({ kind: "typing", error: VIN_COPY.typeError })
    },
    // Back to looking: from typing, or a read / lookup they'd rather not wait for.
    cancel: () => {
      passOver()
      attempt.current++
      setState(IDLE)
    },
  }
}

export type VinStep = ReturnType<typeof useVinStep>

// What a screen reader hears as the VIN step changes (one live region in the
// host, so nothing is missed when a card appears with its text already in it).
export function vinAnnouncement(state: VinState): string {
  switch (state.kind) {
    case "idle":
      return state.note ?? ""
    case "reading":
      return VIN_COPY.reading
    case "looking-up":
      return VIN_COPY.lookingUp
    case "confirm":
      return VIN_COPY.confirm(carName(state.found))
    case "unknown":
      return `${VIN_COPY.unknownTitle}. ${VIN_COPY.unknownBody}`
    case "typing":
      // The field and its error speak for themselves.
      return ""
  }
}
